import PopUpBase from '@components/PopUpBase';
import { pushError, pushSuccess } from '@components/toast';
import { AxiosError } from '@controllers/axiosClient';
import { taskHttpController } from '@controllers/index';
import { ConsolidateCameraMonitoring } from '@EcamModel/model/CameraOverview';
import { CameraTask, TaskStatus } from '@EcamModel/model/CameraTask';
import { IPopUp, UsePopUpReturnType } from '@hooks/usePopUp';
import { LoadingButton } from '@mui/lab';
import { Button, Stack, TextField, Typography } from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { useCameraDetailContext } from '@pages/cameras/hook/CameraDetailsProvider';
import { ellipsisStyles } from '@pages/task-management';
import { CameraSubTasks, SubTaskStatus } from '@pages/task-management/config';
import color from '@theme/Colors';
import _ from 'lodash';
import moment from 'moment';
import { Dispatch, MutableRefObject, SetStateAction, useEffect, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';

type Props = IPopUp & {
    isShowTaskManagement?: boolean;
    taskItem: CameraTask;
    cameraId?: number;
    popupUpsertTask?: UsePopUpReturnType;
    isActionCancelCompletePopup?: MutableRefObject<boolean>;
    onSetTaskItem: (task: CameraTask) => void;
    onChangeTaskItem?: ({ id, taskStatus, subTask }: { id: number; taskStatus: TaskStatus; subTask: string }) => void;
    setCameraConfigMonitoring?: Dispatch<SetStateAction<ConsolidateCameraMonitoring[]>>;
};

export default function PopupCompleteTask(props: Props) {
    const {
        open,
        taskItem,
        cameraId,
        popupUpsertTask,
        isActionCancelCompletePopup,
        onSetTaskItem,
        onChangeTaskItem,
        setCameraConfigMonitoring,
        onClose,
    } = props;
    const { refetchTrailAuditEvent } = useCameraDetailContext();
    const [noteComplete, setNoteComplete] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const handleCancel = () => {
        if (isActionCancelCompletePopup?.current) {
            onSetTaskItem({} as CameraTask);
        } else {
            popupUpsertTask?.setTrue();
            onSetTaskItem(taskItem);
        }
        isActionCancelCompletePopup!.current = false;
        onClose?.();
    };

    const queryClient = useQueryClient();

    const upsertCameraTaskAPI = async (_task: CameraTask) => {
        const response = await taskHttpController.upsert(_task);
        return response;
    };

    const mutationUpsertCameraNote = useMutation(upsertCameraTaskAPI, {
        onSuccess: async (res) => {
            pushSuccess('Task completed successfully');
            queryClient.invalidateQueries(['listTask']);
            if (props.isShowTaskManagement) {
                await refetchTrailAuditEvent();
            }

            onChangeTaskItem?.({ id: res.Id!, taskStatus: res.TaskStatus, subTask: JSON.stringify(res.SubTask) });
            setCameraConfigMonitoring?.((prev) =>
                prev?.map((item) =>
                    item.CameraId === cameraId ? { ...item, CameraTaskCount: item.CameraTaskCount - 1 } : item
                )
            );
            handleCancel();
            popupUpsertTask?.onClose();
        },
        onError: (err: AxiosError) => {
            pushError(err.message);
        },
        onSettled: () => {
            setIsLoading(false);
        },
    });

    const handleConfirmUpsertTask = async () => {
        const parsedSubTasks: CameraSubTasks[] = JSON.parse(taskItem.SubTask ?? '[]');
        const updatedSubTask = parsedSubTasks.map((task) => ({
            ...task,
            Status: SubTaskStatus.Done,
            CompletedAt: _.isNil(task.CompletedAt) ? moment().toDate() : task.CompletedAt,
        }));
        setIsLoading(true);
        const _filter: CameraTask = {
            TaskStatus: TaskStatus.Completed,
            Id: taskItem.Id,
            CompleteDate: moment().toDate(),
            Note: noteComplete,
            SubTask: JSON.stringify(updatedSubTask) ?? undefined,
        } as CameraTask;

        await mutationUpsertCameraNote.mutateAsync(_filter);
    };

    useEffect(() => {
        if (!open) return;
        setNoteComplete('');
    }, [open]);

    return (
        <PopUpBase
            title={
                <Typography
                    variant="h4"
                    sx={{
                        ...ellipsisStyles,
                    }}
                >
                    Complete task: {taskItem?.TaskName}
                </Typography>
            }
            fixOverflow
            dialogProps={{
                fullWidth: true,
                sx: {
                    '& .MuiDialog-paper': {
                        borderRadius: '10px',
                        minWidth: 750,
                    },
                    zIndex: (theme) => theme.zIndex.modal,
                },
            }}
            open={open}
            onClose={onClose}
            hideConfirm
            minWidthButton={130}
            desc={
                <Stack direction={'column'} spacing={3} bgcolor={color.grey100} borderRadius={'4px'} p={2}>
                    <Stack width={'100%'} direction={'row'} spacing={2} alignItems={'center'}>
                        <DesktopDatePicker
                            label={
                                <span>
                                    Due date<span style={{ color: 'red' }}>{`*`}</span>
                                </span>
                            }
                            sx={{
                                borderColor: color.primary,
                                width: '100%',
                            }}
                            format="DD/MM/YYYY"
                            readOnly
                            value={taskItem.EndDate ? moment(taskItem.EndDate) : moment()}
                        />

                        <DesktopDatePicker
                            label={
                                <span>
                                    Complete date<span style={{ color: 'red' }}>{`*`}</span>
                                </span>
                            }
                            sx={{
                                borderColor: color.primary,
                                width: '100%',
                            }}
                            readOnly
                            format="DD/MM/YYYY"
                            value={moment()}
                        />
                    </Stack>

                    <Stack direction={'column'} spacing={1} mt={1}>
                        <TextField
                            autoFocus
                            label="Note"
                            error={noteComplete.length > 1000}
                            multiline
                            sx={{
                                '& .MuiInputBase-input': {
                                    padding: 0,
                                    borderRadius: 0,
                                },
                            }}
                            rows={8}
                            value={noteComplete}
                            onChange={(e) => setNoteComplete(e.target.value)}
                            placeholder="Enter note complete"
                        />
                        <Typography
                            textAlign={'end'}
                            color={noteComplete.length > 1000 ? color.danger : color.textPrimary}
                        >
                            {noteComplete.length} / 1000
                        </Typography>
                    </Stack>
                </Stack>
            }
            customActions={
                <Stack direction={'row'} width="100%" justifyContent="space-between">
                    <Button
                        variant="cancel"
                        onClick={() => {
                            handleCancel();
                        }}
                    >
                        Cancel
                    </Button>
                    <LoadingButton
                        loading={isLoading}
                        loadingPosition="center"
                        variant="contained"
                        disabled={noteComplete.trim().length > 1000 || isLoading}
                        onClick={handleConfirmUpsertTask}
                    >
                        Done
                    </LoadingButton>
                </Stack>
            }
        />
    );
}
